import React, { useState, useRef, useEffect } from 'react'
import Header from './Header.jsx'
import { domain } from './Domain.jsx';
import useFetch from './useFetch.jsx';
import useApiRequest from './useApiRequest.jsx';
import { useLoadingContext } from './LoadingContext.jsx';
import { useLoginContext } from './LoginContext.jsx';
import notify from './Nots.jsx';
import { motion, AnimatePresence } from 'framer-motion';
import { 
    DollarSign, CreditCard, Calendar, Clock, 
    CheckCircle, XCircle, AlertCircle, Trash2, 
    Eye, TrendingUp, Users, Wallet, Filter,
    Search, MoreVertical, Edit, Archive
} from 'lucide-react';

const MyPaymentRequests = () => {
    const { userInfo } = useLoginContext();
    const { setLoading } = useLoadingContext()
    const { deleteRequest, postRequest } = useApiRequest()
    const { data, loading, setLoading:setLoadingData, refresh, setRefresh } = useFetch(`${domain}/payment-requests`)
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 8;

    useEffect(()=>{
        setLoading(loading)
    },[loading])

    // Handle deleting a payment request
    const handleDelete = async(id) => {
        if(window.confirm("Do you want to delete this request ?")){
            const response = await deleteRequest(`${domain}/payment-requests/${id}`)
            setRefresh(!refresh)
        }else{
            notify("Deletion Canceled", "info")
        }
    };

    function formatDate(date) {
        const options = { year: 'numeric', month: 'long', day: 'numeric' };
        return date.toLocaleDateString('en-US', options);
    }

    const handleAction = async(e, method) => {
        const formData = new FormData()
        formData.append("id", e)
        formData.append("action", method)
        const res = await postRequest(`${domain}/approve`, formData, true)
        if(res){
            notify(JSON.stringify(res['message']), res['status'] === "success" ? "info" : "error")
            setRefresh(!refresh)
        }
    }

    // Filter and search functionality
    const filteredData = data?.filter(request => {
        const matchesSearch = request.event.event_plot_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            request.amount.toString().includes(searchTerm) ||
                            request.status.toString().toLowerCase().includes(searchTerm.toLowerCase());
        
        const matchesStatus = statusFilter === 'all' || 
                            (statusFilter === 'approved' && request.status) ||
                            (statusFilter === 'pending' && !request.status);
        
        return matchesSearch && matchesStatus;
    });

    // Pagination
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentRequests = filteredData?.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredData?.length / itemsPerPage);

    const handlePageChange = (page) => {
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // Statistics
    const stats = {
        total: data?.length || 0,
        approved: data?.filter(req => req.status).length || 0,
        pending: data?.filter(req => !req.status).length || 0,
        totalAmount: data?.reduce((sum, req) => sum + req.amount, 0) || 0
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 via-orange-50 to-teal-50">
            <Header />
            
            <div className="container mx-auto px-4 py-8">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="max-w-7xl mx-auto"
                >
                    {/* Header Section */}
                    <div className="mb-8">
                        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                            <div>
                                <h1 className="text-lg font-bold text-gray-800 mb-2">
                                    Payment Requests
                                </h1>
                                <p className="text-sm text-gray-600">
                                    Track and manage your payment requests
                                </p>
                            </div>
                            
                            {/* Quick Stats */}
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                {/* Mobile: Stack vertically, Desktop: Grid */}
                                <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-2xl p-4 border border-orange-200">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-sm font-medium text-orange-800">Total Requests</p>
                                            <p className="text-2xl font-bold text-orange-600 mt-1">{stats.total}</p>
                                        </div>
                                        <div className="w-12 h-12 bg-gradient-to-br from-orange-400 to-orange-500 rounded-xl flex items-center justify-center">
                                            <DollarSign className="w-6 h-6 text-white" />
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl p-4 border border-green-200">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-sm font-medium text-green-800">Approved</p>
                                            <p className="text-2xl font-bold text-green-600 mt-1">{stats.approved}</p>
                                        </div>
                                        <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-green-500 rounded-xl flex items-center justify-center">
                                            <CheckCircle className="w-6 h-6 text-white" />
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-2xl p-4 border border-yellow-200">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-sm font-medium text-yellow-800">Pending</p>
                                            <p className="text-2xl font-bold text-yellow-600 mt-1">{stats.pending}</p>
                                        </div>
                                        <div className="w-12 h-12 bg-gradient-to-br from-yellow-400 to-yellow-500 rounded-xl flex items-center justify-center">
                                            <Clock className="w-6 h-6 text-white" />
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-2xl p-4 border border-teal-200">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-sm font-medium text-teal-800">Total Amount</p>
                                            <p className="text-lg font-bold text-teal-600 mt-1">UGX {stats.totalAmount.toFixed(2)}</p>
                                        </div>
                                        <div className="w-12 h-12 bg-gradient-to-br from-teal-400 to-teal-500 rounded-xl flex items-center justify-center">
                                            <TrendingUp className="w-6 h-6 text-white" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Filters and Search */}
                    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 mb-8">
                        <div className="flex flex-col lg:flex-row gap-4">
                            <div className="flex-1">
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                                    <input
                                        type="text"
                                        placeholder="Search requests..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200"
                                    />
                                </div>
                            </div>
                            
                            <div className="flex gap-4">
                                <select
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                    className="px-3 py-2 text-sm border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200"
                                >
                                    <option value="all">All Status</option>
                                    <option value="approved">Approved</option>
                                    <option value="pending">Pending</option>
                                </select>
                                
                                <button
                                    onClick={() => {
                                        setSearchTerm('');
                                        setStatusFilter('all');
                                        setCurrentPage(1);
                                    }}
                                    className="px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-all duration-200 flex items-center gap-2"
                                >
                                    <Filter className="w-4 h-4" />
                                    Clear
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Requests Grid */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                        {currentRequests && currentRequests.map((request, index) => (
                            <motion.div
                                key={request.id}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.1 }}
                                className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden hover:shadow-xl transition-all duration-300"
                            >
                                <div className="p-6">
                                    {/* Header */}
                                    <div className="flex items-start justify-between mb-4">
                                        <div className="flex items-center gap-3">
                                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                                request.status 
                                                    ? 'bg-green-100' 
                                                    : 'bg-yellow-100'
                                            }`}>
                                                {request.status ? (
                                                    <CheckCircle className="w-5 h-5 text-green-600" />
                                                ) : (
                                                    <Clock className="w-5 h-5 text-yellow-600" />
                                                )}
                                            </div>
                                            <div>
                                                <h3 className="text-lg font-bold text-gray-800 line-clamp-1">
                                                    {request.event.event_plot_name}
                                                </h3>
                                                <p className="text-sm text-gray-500">
                                                    Request #{request.id}
                                                </p>
                                            </div>
                                        </div>
                                        
                                        <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                                            request.status 
                                                ? 'bg-green-100 text-green-700' 
                                                : 'bg-yellow-100 text-yellow-700'
                                        }`}>
                                            {request.status ? "Approved" : "Pending"}
                                        </div>
                                    </div>

                                    {/* Details */}
                                    <div className="space-y-3 mb-6">
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm text-gray-600">Amount Requested</span>
                                            <span className="font-bold text-orange-600">
                                                UGX {request.amount.toFixed(2)}
                                            </span>
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm text-gray-600">Total Collected</span>
                                            <span className="font-semibold text-teal-600">
                                                UGX {request.total_available.toFixed(2)}
                                            </span>
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm text-gray-600">Request Date</span>
                                            <span className="text-sm text-gray-700">
                                                {formatDate(new Date(request.created_at))}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Actions */}
                                    <div className="flex gap-2">
                                        {!request.status && (
                                            <button
                                                onClick={() => handleDelete(request.id)}
                                                className="flex-1 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg font-medium transition-all duration-200 flex items-center justify-center gap-2"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                                Delete
                                            </button>
                                        )}
                                        
                                        {userInfo && userInfo.user.owner.is_staff === true && userInfo.user.owner.is_superuser === true && !request.status && (
                                            <>
                                                <button
                                                    onClick={() => handleAction(request.id, "approve")}
                                                    className="flex-1 px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium transition-all duration-200 flex items-center justify-center gap-2"
                                                >
                                                    <CheckCircle className="w-4 h-4" />
                                                    Approve
                                                </button>
                                                <button
                                                    onClick={() => handleAction(request.id, "cancel")}
                                                    className="flex-1 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg font-medium transition-all duration-200 flex items-center justify-center gap-2"
                                                >
                                                    <XCircle className="w-4 h-4" />
                                                    Deny
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>

                    {/* Empty State */}
                    {currentRequests && currentRequests.length === 0 && (
                        <motion.div
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="text-center py-12"
                        >
                            <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Wallet className="w-12 h-12 text-gray-400" />
                            </div>
                            <h3 className="text-lg font-semibold text-gray-800 mb-2">No Payment Requests</h3>
                            <p className="text-sm text-gray-600">
                                {searchTerm || statusFilter !== 'all' 
                                    ? "No requests match your current filters." 
                                    : "You haven't made any payment requests yet."
                                }
                            </p>
                        </motion.div>
                    )}

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="flex items-center justify-center gap-2 mt-8">
                            <button
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                                className="px-4 py-2 border border-orange-300 text-orange-600 rounded-lg hover:bg-orange-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                            >
                                Previous
                            </button>
                            {[...Array(totalPages).keys()].map(page => {
                                const pageNum = page + 1;
                                const isCurrent = currentPage === pageNum;
                                return (
                                    <button
                                        key={pageNum}
                                        onClick={() => handlePageChange(pageNum)}
                                        className={`px-4 py-2 rounded-lg font-medium transition-colors duration-200 ${
                                            isCurrent 
                                                ? 'bg-orange-500 text-white' 
                                                : 'border border-orange-300 text-orange-600 hover:bg-orange-50'
                                        }`}
                                    >
                                        {pageNum}
                                    </button>
                                );
                            })}
                            <button
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === totalPages}
                                className="px-4 py-2 border border-orange-300 text-orange-600 rounded-lg hover:bg-orange-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                            >
                                Next
                            </button>
                </div>
                    )}
                </motion.div>
            </div>
        </div>
    )
}

export default MyPaymentRequests